// tippy -- the vendored v5 global (vendor/assets/javascripts/tippy) -- hides a
// `hideOnClick: true` popover on any document mousedown outside its popper,
// judged by DOM containment. A layer that renders above popovers, such as a
// bottom sheet teleported to <body>, is a logical descendant of the popover
// that opened it but never a DOM one, so every tap in it would close its host.
// v5 has no hook for the outside press itself, but `hide()` is cancelled when
// `onHide` returns false. The press is recorded first, in a capture-phase
// document listener registered before any tippy instance adds its own, so
// `onHide` can tell a tap inside an elevated layer from every other hide.

/** Attribute an overlay sets on its root to declare it renders above popovers. */
export const ELEVATED_LAYER = "[data-elevated-layer]";

type PopoverInstance = { hide(): void };
type PopoverOptions = {
  hideOnClick?: boolean | "toggle";
  onHide?: (instance: PopoverInstance) => unknown;
};

let pressingInsideElevatedLayer = false;
let tracking = false;

const trackPress = (event: Event) => {
  const target = event.target;
  pressingInsideElevatedLayer =
    target instanceof Element && target.closest(ELEVATED_LAYER) !== null;
  // tippy hides synchronously inside the same mousedown dispatch, so the flag
  // only needs to survive until the event has finished propagating.
  if (pressingInsideElevatedLayer) {
    setTimeout(() => {
      pressingInsideElevatedLayer = false;
    });
  }
};

const ensureTracking = () => {
  if (tracking || typeof document === "undefined") return;
  document.addEventListener("mousedown", trackPress, true);
  tracking = true;
};

/**
 * Wrap tippy options so a `hideOnClick: true` popover stays open while the
 * user presses inside an elevated layer. Other options pass through untouched.
 */
export function guardHideOnClick<T extends PopoverOptions>(options: T): T {
  if (options.hideOnClick !== true) return options;
  ensureTracking();
  const { onHide } = options;
  return {
    ...options,
    onHide(instance: PopoverInstance) {
      if (pressingInsideElevatedLayer) return false;
      return onHide ? onHide(instance) : undefined;
    }
  };
}
